import React from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { COLOR } from "../utils/format";

const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

/** Small round paperclip trigger with a count badge when there's more than one file. */
export const AttachmentButton: React.FC<{ count: number; onClick: () => void }> = ({ count, onClick }) => {
  if (count === 0) return null;
  return (
    <Tooltip title={count > 1 ? `View ${count} attachments` : "View attachment"}>
      <Box
       
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: COLOR.blueBg,
          color: COLOR.blue,
          cursor: "pointer",
          flexShrink: 0,
          transition: "background-color 0.15s, transform 0.15s",
          "&:hover": { backgroundColor: COLOR.blue, color: "#fff", transform: "scale(1.08)" },
        }}
      >
        <AttachFileIcon sx={{ fontSize: 13 }} />
        {count > 1 && (
          <Box
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 15,
              height: 15,
              px: "3px",
              borderRadius: "8px",
              backgroundColor: COLOR.blue,
              color: "#fff",
              fontSize: "0.6rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid #fff",
              lineHeight: 1,
            }}
          >
            {count}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

interface ViewerProps {
  files: string[]; // storage paths; empty = closed
  onClose: () => void;
}

/** Paged attachment viewer (images and PDFs), same pattern as the Income module. */
export const AttachmentViewer: React.FC<ViewerProps> = ({ files, onClose }) => {
  const [index, setIndex] = React.useState(0);
  React.useEffect(() => setIndex(0), [files]);

  const url = files.length ? `${LARAVEL_API_URL}/${files[index]}` : "";
  const navButton = (dir: -1 | 1) => (
    <IconButton
      onClick={() => setIndex((i) => i + dir)}
      disabled={dir < 0 ? index === 0 : index === files.length - 1}
      sx={{
        position: "absolute",
        [dir < 0 ? "left" : "right"]: 4,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 1,
        backgroundColor: "rgba(255,255,255,0.85)",
        "&:hover": { backgroundColor: "#fff" },
      }}
    >
      {dir < 0 ? "‹" : "›"}
    </IconButton>
  );

  return (
    <Dialog open={files.length > 0} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <span>Attachment</span>
          {files.length > 1 && <Chip size="small" label={`${index + 1} of ${files.length}`} />}
        </Box>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ position: "relative" }}>
          {files.length > 1 && navButton(-1)}
          {url.toLowerCase().endsWith(".pdf") ? (
            <iframe src={url} title="PDF" style={{ width: "100%", height: 500, border: "none" }} />
          ) : (
            url && <img src={url} alt="Attachment" style={{ width: "100%", maxHeight: 500, objectFit: "contain" }} />
          )}
          {files.length > 1 && navButton(1)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
